import { useEffect, useState } from 'react';
import TeamCard from '../TeamCard/TeamCard';// adjust path as needed
import styles from './teamLinks.module.css';
export default function Abteilungen() {
    const [seniorinnen, setSeniorinnen] = useState([]);
    const [juniorinnen, setJuniorinnen] = useState([]);
    const [sonstige, setSonstige] = useState([]);

    useEffect(() => {
        fetch('/api/team/teamlinks')
            .then(res => res.json())
            .then(data => {
                const juniors = [];
                const seniors = [];
                const others = [];
                const transformed = data.map(team => {
                    const {
                        name,
                        url_slug,
                        football_cat,
                        junioren_cat,
                        team_img,
                        current_league
                    } = team;

                    // Determine liga
                    let liga = current_league || '';
                    if (football_cat === 'Junioren' || football_cat === 'Sonstige') {
                        liga = junioren_cat || '';
                    }

                    // Determine image
                    let img = '/uploads/unsplash/vikram-tkv-JO19K0HDDXI-unsplash.jpg';
                    if (team_img) {
                        img = team_img;
                    } else if (junioren_cat === 'Talentwerk') {
                        img = '/uploads/unsplash/rachel-yoon-6sXVajxknWU-unsplash.jpg';
                    }

                    return {
                        team: name,
                        liga,
                        link: `/mannschaftssport/fußball/${url_slug}`,
                        img,
                        altText: `Teamfoto, ${name} Fußball, Oberpleis`,
                        football_cat, // Keep this temporarily for sorting
                    };
                });

                // Distribute into categories
                transformed.forEach(team => {
                    switch (team.football_cat) {
                        case 'Junioren':
                            juniors.push(team);
                            break;
                        case 'Senioren':
                            seniors.push(team);
                            break;
                        case 'Sonstige':
                            others.push(team);
                            break;
                        default:
                            break;
                    }
                });
                setJuniorinnen(sortJunioren(juniors));
                setSeniorinnen(sortSenioren(seniors));
                setSonstige(sortSonstige(others));
            });
    }, []);

    function extractNumber(teamName) {
        const match = teamName.match(/(\d+)\.?\s?(Herren|Damen)/i);
        return match ? parseInt(match[1], 10) : 999;
    }

    // ---------- helpers ----------
    function romanToInt(roman) {
        const map = { I: 1, II: 2, III: 3, IV: 4, V: 5, VI: 6, VII: 7, VIII: 8, IX: 9, X: 10 };
        return map[roman.toUpperCase()] ?? 0;
    }

    // Find the *largest* age mentioned in names like:
    // "U19", "U15 (Juniorinnen)", "U9.2", "U5/U6", "Bambini U5/U6", "U11 II", "U11 I U10"
    function parseJuniorAge(name) {
        // Bambini → treat as youngest group (≈ U5)
        if (/bambini/i.test(name)) return 5;

        // Grab all numbers that follow a U (handles U9.2, U5/U6, U11, etc.)
        // e.g. "U9.2" -> "9", "U5/U6" -> ["5","6"]
        const matches = [...name.matchAll(/U\s*(\d+)/gi)].map(m => parseInt(m[1], 10));
        if (matches.length) return Math.max(...matches);

        return null; // nothing found
    }

    // Optional: when same U-age, use any roman suffix to order I < II < III etc.
    function parseJuniorRomanPart(name) {
        const m = name.match(/\b(U\s*\d+)\s*([IVX]{1,4})\b/i);
        return m ? romanToInt(m[2]) : 0;
    }

    // For Senioren like "Herren 1", "Damen 2", "Herren II"
    function parseSeniorGroup(name) {
        const isHerren = /herren/i.test(name);
        const isDamen = /damen/i.test(name);
        return isHerren ? 0 : isDamen ? 1 : 2; // Herren first, then Damen, then other
    }

    function parseSeniorNumber(name) {
        // Prefer a number near Herren/Damen, else any standalone number, else roman
        const near = name.match(/(?:herren|damen)\s*(\d+)/i);
        if (near) return parseInt(near[1], 10);

        const anyNum = name.match(/\b(\d+)\b/);
        if (anyNum) return parseInt(anyNum[1], 10);

        const roman = name.match(/\b([IVX]{1,4})\b/i);
        if (roman) return romanToInt(roman[1]);

        return Number.POSITIVE_INFINITY; // no number → send to end of its group
    }

    function naturalNameCompare(a, b) {
        // German locale; numeric so "U9" < "U10"
        return a.localeCompare(b, 'de', { numeric: true, sensitivity: 'base' });
    }


    function sortJunioren(teams) {
        return [...teams].sort((a, b) => {
            const nameA = a.team || '';
            const nameB = b.team || '';

            const ageA = parseJuniorAge(nameA);
            const ageB = parseJuniorAge(nameB);

            if (ageA !== null && ageB !== null) {
                if (ageA !== ageB) return ageB - ageA; // higher age first: U19 > U17
                // same age → order by roman part (I < II), then name
                const rA = parseJuniorRomanPart(nameA);
                const rB = parseJuniorRomanPart(nameB);
                if (rA !== rB) return rA - rB;
                return naturalNameCompare(nameA, nameB);
            }
            if (ageA !== null) return -1; // ones with a parsed age come first
            if (ageB !== null) return 1;
            return naturalNameCompare(nameA, nameB);
        });
    }

    function sortSenioren(teams) {
        return [...teams].sort((a, b) => {
            const nameA = a.team || '';
            const nameB = b.team || '';

            const grpA = parseSeniorGroup(nameA);
            const grpB = parseSeniorGroup(nameB);
            if (grpA !== grpB) return grpA - grpB; // Herren before Damen before other

            const numA = parseSeniorNumber(nameA);
            const numB = parseSeniorNumber(nameB);
            if (numA !== numB) return numA - numB; // 1, 2, 3…

            return naturalNameCompare(nameA, nameB);
        });
    }

    function sortSonstige(teams) {
        return [...teams].sort((a, b) => naturalNameCompare(a.team || '', b.team || ''));
    }

    return (
        <section id='abteilungen'>
            <h2>Abteilungen</h2>
            <div className={styles.abteilungDiv}>
                <section id='senior-innen'>
                    <h3>Senior:innen</h3>
                    <div className={styles.abteilungenSeniorinnen}>
                        {seniorinnen.map((item, index) => (
                            <TeamCard key={index} item={item} />
                        ))}
                    </div>
                </section>
                <section id='junior-innen'>
                    <h3>Junior:innen</h3>
                    <div className={styles.abteilungen}>
                        {juniorinnen.map((item, index) => (
                            <TeamCard key={index} item={item} />
                        ))}
                    </div>
                </section>
                <section id='alte-herren-walking-football'>
                    <h3>Alte Herren und Walking Football</h3>
                    <div className={styles.abteilungen}>
                        {sonstige.map((item, index) => (
                            <TeamCard key={index} item={item} />
                        ))}
                    </div>
                </section>
            </div>
        </section>
    );
}
